<template>
  <div class="p-4 max-w-6xl mx-auto space-y-6">
    <div>
      <h2 class="text-2xl font-bold mb-1">📅 Mi Horario de Trabajo</h2>
      <p class="text-sm text-gray-500">
        Define tu plantilla semanal, bloquea días específicos y verifica en el calendario que todo coincida con hora local de Puerto Rico.
      </p>
      <div
        v-if="resumenHorarios"
        class="mt-4 px-4 py-3 rounded-lg text-sm"
        :class="resumenHorarios.bloquesFuturos < bloquesAlerta ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'"
      >
        <p class="font-semibold">
          {{ resumenHorarios.bloquesFuturos }} bloques disponibles
        </p>
        <p>Próxima disponibilidad: {{ formatearResumenFecha(resumenHorarios.primeraDisponibilidad) }}</p>
        <p v-if="resumenHorarios.bloquesFuturos < bloquesAlerta">
          Pulsa "Regenerar calendario" para extender tus horarios.
        </p>
      </div>
    </div>

    <div class="border border-slate-200 rounded-lg bg-white shadow-sm p-4">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3 class="text-lg font-semibold text-slate-800">Plantilla semanal</h3>
          <p class="text-sm text-slate-500">
            Activa los días y define intervalos AM/PM. Cada cambio regenerará las próximas semanas automáticamente.
          </p>
        </div>
        <span v-if="aplicandoPlantilla" class="text-xs text-emerald-600 font-semibold">Aplicando plantilla...</span>
      </div>
      <HorarioPlantilla
        v-if="usuarioId"
        :usuario-id="usuarioId"
        @plantilla-guardada="aplicarPlantilla"
      />
      <p v-else class="text-sm text-slate-400">Conecta tu sesión para editar tu plantilla.</p>
    </div>

    <div class="grid gap-4 md:grid-cols-3">
      <div class="bg-white rounded-lg shadow p-4 border border-gray-100">
        <h3 class="text-lg font-semibold mb-2">Bloquear día completo</h3>
        <p class="text-sm text-gray-500 mb-3">Registra una ausencia en cualquier día para que desaparezca de la agenda.</p>
        <input type="date" v-model="diaParaBloquear" class="w-full border rounded px-3 py-2 mb-3 focus:outline-none focus:ring" />
        <button
          class="w-full bg-red-600 text-white py-2 rounded hover:bg-red-700 disabled:opacity-50"
          :disabled="bloqueando"
          @click="bloquearDia"
        >
          {{ bloqueando ? "Procesando..." : "Bloquear día" }}
        </button>
      </div>
      <div class="bg-white rounded-lg shadow p-4 border border-gray-100">
        <h3 class="text-lg font-semibold mb-2">Habilitar día bloqueado</h3>
        <p class="text-sm text-gray-500 mb-3">Remueve excepciones registradas para volver a mostrar la disponibilidad.</p>
        <input type="date" v-model="diaParaHabilitar" class="w-full border rounded px-3 py-2 mb-3 focus:outline-none focus:ring" />
        <button
          class="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700 disabled:opacity-50"
          :disabled="habilitando"
          @click="habilitarDia"
        >
          {{ habilitando ? "Procesando..." : "Habilitar día" }}
        </button>
      </div>
      <div class="bg-white rounded-lg shadow p-4 border border-gray-100">
        <h3 class="text-lg font-semibold mb-2 text-slate-800">Regenerar calendario</h3>
        <p class="text-sm text-gray-500 mb-3">Limpia todos los bloques generados por tu plantilla y vuelve a crear las próximas semanas.</p>
        <button
          class="w-full bg-slate-700 text-white py-2 rounded hover:bg-slate-800 disabled:opacity-50"
          :disabled="regenerando"
          @click="regenerarCalendario"
        >
          {{ regenerando ? "Regenerando..." : "Limpiar y regenerar calendario" }}
        </button>
      </div>
    </div>

    <div class="space-y-4">
      <div class="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
        <p class="text-sm text-slate-500">
          Tus bloques futuros aparecen agrupados por día. Ajusta, pausa o elimina desde aquí sin abrir el calendario.
        </p>
      </div>

      <div v-if="diasOrdenados.length" class="space-y-4">
        <section
          v-for="fecha in diasOrdenados"
          :key="fecha"
          class="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3"
        >
          <div class="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
            <div>
              <p class="text-xs uppercase font-semibold text-slate-400">
                {{ formatearFechaHumana(fecha) }}
              </p>
              <p class="text-lg font-semibold text-slate-900">{{ fecha }}</p>
            </div>
            <div class="flex gap-2">
              <button
                class="px-3 py-1.5 rounded-lg text-sm font-semibold border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                :disabled="bloqueando"
                @click="pausarFecha(fecha)"
              >
                {{ bloqueando ? "Pausando..." : "Pausar día" }}
              </button>
              <button
                class="px-3 py-1.5 rounded-lg text-sm font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                :disabled="habilitando"
                @click="habilitarFecha(fecha)"
              >
                {{ habilitando ? "Habilitando..." : "Habilitar día" }}
              </button>
            </div>
          </div>

          <div v-if="(disponibilidadPorDia[fecha] || []).length" class="space-y-3">
            <article
              v-for="bloque in disponibilidadPorDia[fecha]"
              :key="bloque.id"
              class="border border-slate-100 rounded-lg p-3 space-y-3"
            >
              <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <p class="text-base font-semibold text-slate-900">
                  {{ formatearHoraBloque(bloque.start) }} – {{ formatearHoraBloque(bloque.end) }}
                </p>
                <div class="flex flex-wrap gap-2">
                  <button
                    class="px-3 py-1.5 rounded-lg text-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
                    @click="iniciarEdicion(bloque)"
                  >
                    Editar
                  </button>
                  <button
                    class="px-3 py-1.5 rounded-lg text-sm border border-red-300 text-red-700 hover:bg-red-50"
                    @click="eliminarBloque(bloque)"
                  >
                    Eliminar
                  </button>
                </div>
              </div>

              <div v-if="edicionActiva === bloque.id" class="grid gap-3 md:grid-cols-5">
                <label class="flex flex-col gap-1 text-sm">
                  <span class="text-xs font-semibold text-slate-500">Desde</span>
                  <input
                    type="time"
                    v-model="formEdicion.desde"
                    class="border rounded-lg px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </label>
                <label class="flex flex-col gap-1 text-sm">
                  <span class="text-xs font-semibold text-slate-500">Hasta</span>
                  <input
                    type="time"
                    v-model="formEdicion.hasta"
                    class="border rounded-lg px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </label>
                <div class="flex items-end gap-2 md:col-span-3">
                  <button
                    class="flex-1 bg-emerald-600 text-white py-2 rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50"
                    :disabled="guardandoBloque"
                    @click="guardarEdicion"
                  >
                    Guardar cambios
                  </button>
                  <button
                    class="flex-1 bg-slate-100 text-slate-700 py-2 rounded-lg font-semibold hover:bg-slate-200"
                    @click="cancelarEdicion"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </article>
          </div>
          <p v-else class="text-sm text-slate-500">Sin bloques disponibles en esta fecha.</p>
        </section>
      </div>

      <p v-else class="text-center text-slate-400 py-12">
        No tienes horarios generados en las próximas semanas.
      </p>
    </div>
  </div>
</template>

<script>
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { formatIsoPrTime } from "@/utils/prTime";
import HorarioPlantilla from "../HorarioPlantilla.vue";
const BLOQUES_ALERTA = 10;

export default {
  name: "MiHorario",
  components: { HorarioPlantilla },
  computed: {
    diasOrdenados() {
      return Object.keys(this.disponibilidadPorDia || {}).sort();
    },
  },
  data() {
    return {
      usuarioId: "",
      authUnsubscribe: null,
      functionsInstance: null,
      aplicandoPlantilla: false,
      eventos: [],
      disponibilidadPorDia: {},
      diaParaBloquear: "",
      diaParaHabilitar: "",
      bloqueando: false,
      habilitando: false,
      regenerando: false,
      resumenHorarios: null,
      verificandoResumen: false,
      bloquesAlerta: BLOQUES_ALERTA,
      edicionActiva: null,
      guardandoBloque: false,
      formEdicion: {
        desde: "",
        hasta: "",
        fecha: "",
        bloqueId: "",
      },
    };
  },
  methods: {
    getCallable(nombre) {
      if (!this.functionsInstance) {
        this.functionsInstance = getFunctions();
      }
      return httpsCallable(this.functionsInstance, nombre);
    },
    async invocarCallable(nombre, payload) {
      const callable = this.getCallable(nombre);
      const respuesta = await callable(payload);
      return respuesta?.data;
    },
    async inicializarAuth() {
      const auth = getAuth();
      this.authUnsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          this.usuarioId = user.uid;
          this.fetchDisponibilidad();
        }
      });
    },
    async fetchDisponibilidad() {
      if (!this.usuarioId) return;
      try {
        const payload = await this.invocarCallable("listarDisponibilidadCallable", {
          uid: this.usuarioId,
          soloFuturos: false,
          origenesPermitidos: ["auto", "excepcion"],
        });
        const bloques = payload?.porEnfermero?.[this.usuarioId] || [];
        const eventos = bloques.map((data) => ({
          id: data.id,
          ...data,
          start: data.start,
          end: data.end,
          title: data.title || "Disponible",
          color: data.repetir ? "#f59e0b" : "#3b82f6",
          textColor: "#ffffff",
        }));
        this.eventos = eventos;
        this.actualizarDisponibilidadPorDia();
        await this.cargarResumen();
      } catch (error) {
        console.error("Error al cargar disponibilidad:", error);
        alert("No se pudo cargar tu disponibilidad.");
      }
    },
    actualizarDisponibilidadPorDia() {
      const mapa = {};
      (this.eventos || []).forEach((bloque) => {
        const fecha =
          bloque.fechaKey ||
          (bloque.start || "").split("T")[0] ||
          "";
        if (!fecha) return;
        if (!mapa[fecha]) mapa[fecha] = [];
        mapa[fecha].push(bloque);
      });
      Object.keys(mapa).forEach((fecha) => {
        mapa[fecha].sort(
          (a, b) => new Date(a.start || 0).getTime() - new Date(b.start || 0).getTime()
        );
      });
      this.disponibilidadPorDia = mapa;
    },
    async aplicarPlantilla() {
      if (!this.usuarioId) return;
      this.aplicandoPlantilla = true;
      try {
        await this.invocarCallable("regenerarHorariosCallable", {
          uid: this.usuarioId,
        });
        await this.fetchDisponibilidad();
        alert("Tu horario semanal se actualizó correctamente.");
      } catch (error) {
        console.error("Error aplicando plantilla:", error);
        alert("No se pudo aplicar la plantilla. Intenta nuevamente.");
      } finally {
        this.aplicandoPlantilla = false;
      }
    },
    formatearFechaHumana(fecha) {
      if (!fecha) return "Sin fecha";
      const d = new Date(`${fecha}T12:00:00Z`);
      if (Number.isNaN(d.getTime())) return fecha;
      return d.toLocaleDateString("es-PR", {
        timeZone: "America/Puerto_Rico",
        weekday: "long",
        month: "short",
        day: "numeric",
      });
    },
    formatearHoraBloque(iso) {
      if (!iso) return "--";
      const valor = formatIsoPrTime(iso, { hour12: true });
      return valor || "--";
    },
    iniciarEdicion(bloque) {
      const fecha =
        bloque.fechaKey ||
        (bloque.start || "").split("T")[0] ||
        "";
      this.edicionActiva = bloque.id;
      this.formEdicion = {
        desde: formatIsoPrTime(bloque.start || "", {}),
        hasta: formatIsoPrTime(bloque.end || "", {}),
        fecha,
        bloqueId: bloque.id,
      };
    },
    cancelarEdicion() {
      this.edicionActiva = null;
      this.formEdicion = {
        desde: "",
        hasta: "",
        fecha: "",
        bloqueId: "",
      };
    },
    async guardarEdicion() {
      if (!this.usuarioId || !this.edicionActiva) return;
      const { desde, hasta, fecha, bloqueId } = this.formEdicion;
      if (!desde || !hasta || !fecha) {
        alert("Completa las horas antes de guardar.");
        return;
      }
      if (desde >= hasta) {
        alert("La hora final debe ser mayor a la inicial.");
        return;
      }
      this.guardandoBloque = true;
      try {
        await this.invocarCallable("actualizarBloqueCallable", {
          uid: this.usuarioId,
          disponibilidadId: bloqueId,
          start: `${fecha}T${desde}:00`,
          end: `${fecha}T${hasta}:00`,
        });
        this.cancelarEdicion();
        await this.fetchDisponibilidad();
      } catch (error) {
        console.error("Error al actualizar bloque:", error);
        alert("No se pudo actualizar el bloque.");
      } finally {
        this.guardandoBloque = false;
      }
    },
    async eliminarBloque(bloque) {
      if (!this.usuarioId) return;
      const confirmar = confirm("¿Eliminar este bloque disponible?");
      if (!confirmar) return;
      try {
        await this.invocarCallable("eliminarBloqueCallable", {
          uid: this.usuarioId,
          disponibilidadId: bloque.id,
        });
        await this.fetchDisponibilidad();
      } catch (error) {
        console.error("Error al eliminar bloque:", error);
        alert("No se pudo eliminar el bloque.");
      }
    },
    async pausarFecha(fecha) {
      this.diaParaBloquear = fecha;
      await this.bloquearDia();
    },
    async habilitarFecha(fecha) {
      this.diaParaHabilitar = fecha;
      await this.habilitarDia();
    },
    async regenerarCalendario() {
      if (!this.usuarioId) return;
      const confirmar = window.confirm(
        "Esto eliminará los bloques generados automáticamente y aplicará nuevamente la plantilla. ¿Deseas continuar?"
      );
      if (!confirmar) return;
      this.regenerando = true;
      try {
        await this.invocarCallable("regenerarHorariosCallable", {
          uid: this.usuarioId,
        });
        await this.fetchDisponibilidad();
        alert("Calendario regenerado con éxito.");
      } catch (error) {
        console.error("Error al regenerar:", error);
        alert("No se pudo regenerar el calendario. Intenta nuevamente.");
      } finally {
        this.regenerando = false;
      }
    },
    async cargarResumen() {
      if (!this.usuarioId) {
        this.resumenHorarios = null;
        return;
      }
      this.verificandoResumen = true;
      try {
        const payload = await this.invocarCallable("verificarHorariosCallable", {
          uids: [this.usuarioId],
          semanas: 2,
        });
        this.resumenHorarios = payload?.resultados?.[0] || null;
      } catch (error) {
        console.error("Error auditando horarios:", error);
        this.resumenHorarios = null;
      } finally {
        this.verificandoResumen = false;
      }
    },
    formatearResumenFecha(iso) {
      if (!iso) return "N/A";
      const fecha = new Date(iso);
      if (Number.isNaN(fecha.getTime())) return "N/A";
      return fecha.toLocaleString("es-PR", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    async bloquearDia() {
      if (!this.usuarioId || !this.diaParaBloquear) {
        alert("Selecciona la fecha que deseas bloquear.");
        return;
      }
      this.bloqueando = true;
      try {
        await this.invocarCallable("bloquearDiaCallable", {
          uid: this.usuarioId,
          fecha: this.diaParaBloquear,
        });
        await this.fetchDisponibilidad();
        alert("Día bloqueado exitosamente.");
      } catch (error) {
        console.error("Error al bloquear día:", error);
        alert("No se pudo bloquear el día. Intenta nuevamente.");
      } finally {
        this.bloqueando = false;
      }
    },
    async habilitarDia() {
      if (!this.usuarioId || !this.diaParaHabilitar) {
        alert("Selecciona la fecha que deseas habilitar.");
        return;
      }
      this.habilitando = true;
      try {
        await this.invocarCallable("habilitarDiaCallable", {
          uid: this.usuarioId,
          fecha: this.diaParaHabilitar,
        });
        await this.fetchDisponibilidad();
        alert("Día habilitado nuevamente.");
      } catch (error) {
        console.error("Error al habilitar día:", error);
        alert("No se pudo habilitar el día. Intenta nuevamente.");
      } finally {
        this.habilitando = false;
      }
    },
  },
  mounted() {
    this.inicializarAuth();
  },
  beforeUnmount() {
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
    }
  },
};
</script>
